// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { createReference, type MedplumClient } from '@medplum/core';
import type { Coverage, DocumentReference, Patient, Practitioner, ServiceRequest } from '@medplum/fhirtypes';
import polarisLogo from '../assets/polaris-logo.svg?raw';
import { withTenantScope } from './tenant';

export const IN_LAB_PSG_CPT_CODE = '95810';
export const IN_LAB_PSG_SERVICE_TEXT = 'Attended, in-laboratory diagnostic sleep study (polysomnography)';
export const IN_LAB_PSG_DOCUMENT_TITLE = 'Sleep Study Order Prescription — In-Lab PSG';
export const HSAT_OTHER_CPT_CODE = '95800';
export const HSAT_OTHER_DOCUMENT_TITLE = 'Sleep Study Order Prescription — HSAT';

const POLARIS_SLEEP_ADDRESS = '1032 15th St NW #418';
const POLARIS_SLEEP_CITY_STATE_ZIP = 'Washington, DC 20005';
const POLARIS_SLEEP_PHONE = '(301) 246-2152';
const POLARIS_SLEEP_FAX = '(260) 668-2001';
const POLARIS_SLEEP_EIN = '41-4815927';

export interface PsgCoverageData {
  payer: string;
  plan: string;
  memberId: string;
  group: string;
}

export interface PsgOrderPdfData {
  patient: Patient;
  practitioner: Practitioner;
  primaryCoverage?: PsgCoverageData;
  secondaryCoverage?: PsgCoverageData;
  priority: string;
  orderDate: string;
  diagnosis: string;
  diagnosisCode?: string;
  secondaryDiagnosis?: string;
  secondaryDiagnosisCode?: string;
  rationale: string;
  supportingIndications: string[];
  notes?: string;
  signature: string;
  signedAt: string;
  orderType?: 'in-lab-psg' | 'hsat-other';
  hsatVendor?: string;
}

function value(value: string | undefined): string {
  return value?.trim() || '';
}

function formatDate(date: string | undefined): string {
  const normalized = value(date);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalized);
  if (!match) {
    return normalized;
  }
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatGender(gender: string | undefined): string {
  const normalized = value(gender);
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : '';
}

function patientName(patient: Patient): string {
  const name = patient.name?.[0];
  return value([...((name?.given ?? []) as string[]), name?.family].filter(Boolean).join(' '));
}

function providerName(practitioner: Practitioner): string {
  const name = practitioner.name?.[0];
  return value([...((name?.given ?? []) as string[]), name?.family].filter(Boolean).join(' '));
}

function field(label: string, text: string): Record<string, unknown> {
  return {
    stack: [
      { text: label.toUpperCase(), style: 'fieldLabel' },
      { text: value(text) || '—', style: 'fieldValue' },
    ],
    margin: [0, 0, 10, 5],
  };
}

function sectionLabel(text: string): Record<string, unknown> {
  return { text: text.toUpperCase(), style: 'sectionHeading' };
}

export function buildPsgOrderPdfDefinition(data: PsgOrderPdfData, logoDataUri?: string): Record<string, unknown> {
  const isOtherHsat = data.orderType === 'hsat-other';
  const testType = isOtherHsat ? `HSAT — ${value(data.hsatVendor)}` : 'In-Lab';
  const cptCode = isOtherHsat ? HSAT_OTHER_CPT_CODE : IN_LAB_PSG_CPT_CODE;
  const serviceText = isOtherHsat
    ? `Home sleep apnea test (HSAT) — ${value(data.hsatVendor)}`
    : IN_LAB_PSG_SERVICE_TEXT;
  const calloutLayout = {
    hLineWidth: () => 0,
    vLineWidth: (index: number) => (index === 0 ? 3 : 0),
    vLineColor: () => '#640040',
    paddingLeft: () => 0,
    paddingRight: () => 0,
    paddingTop: () => 0,
    paddingBottom: () => 0,
  };
  const fieldGridLayout = {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: () => 0,
    paddingRight: () => 0,
    paddingTop: () => 0,
    paddingBottom: () => 0,
  };
  const patientPhone = data.patient.telecom?.find((entry) => entry.system === 'phone')?.value;
  const practitionerPhone = data.practitioner.telecom?.find((entry) => entry.system === 'phone')?.value;
  const practitionerNpi = data.practitioner.identifier?.find((entry) => entry.system?.includes('npi'))?.value;
  const indications = data.supportingIndications.length ? data.supportingIndications.join('\n') : '';

  return {
    pageSize: 'LETTER',
    pageMargins: [48, 36, 48, 36],
    ...(logoDataUri ? { images: { polarisLogo: logoDataUri } } : {}),
    content: [
      {
        columns: [
          {
            stack: [
              ...(logoDataUri
                ? [{ image: 'polarisLogo', width: 147, height: 28 }]
                : [{ text: 'POLARIS', style: 'brand' }]),
              { text: 'SLEEP MEDICINE', style: 'subtitle' },
            ],
          },
          {
            text: `${POLARIS_SLEEP_ADDRESS}\n${POLARIS_SLEEP_CITY_STATE_ZIP}\n${POLARIS_SLEEP_PHONE} · fax ${POLARIS_SLEEP_FAX}\nEIN ${POLARIS_SLEEP_EIN}`,
            alignment: 'right',
            style: 'clinicDetails',
          },
        ],
        margin: [0, 0, 0, 14],
      },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 516, y2: 0, lineWidth: 2, lineColor: '#640040' }] },
      { text: 'Sleep Study Order — Prescription', style: 'documentTitle' },
      { text: data.orderDate, style: 'documentDate' },
      sectionLabel('Patient and order'),
      {
        columns: [
          field('Name', patientName(data.patient)),
          field('Date of birth', formatDate(data.patient.birthDate)),
          field('Sex', formatGender(data.patient.gender)),
          field('Phone', patientPhone ?? ''),
        ],
      },
      {
        columns: [field('Test type', testType), field('Priority', data.priority), field('Order date', data.orderDate)],
      },
      {
        table: {
          widths: ['*'],
          body: [[{ text: `${serviceText}. CPT ${cptCode}.`, style: 'orderCallout' }]],
        },
        layout: calloutLayout,
      },
      sectionLabel('Insurance'),
      ...(isOtherHsat
        ? [{ columns: [field('Insurance', data.primaryCoverage?.payer ?? '')] }]
        : [
            {
              columns: [
                field('Insurance', data.primaryCoverage?.payer ?? ''),
                field('Member ID', data.primaryCoverage?.memberId ?? ''),
                field('Group', data.primaryCoverage?.group ?? ''),
                field('Secondary coverage', data.secondaryCoverage?.payer ?? 'None on file'),
              ],
            },
          ]),
      sectionLabel('Clinical rationale'),
      {
        table: { widths: ['*'], body: [[{ columns: [field('Rationale', data.rationale)] }]] },
        layout: fieldGridLayout,
      },
      {
        table: { widths: ['*'], body: [[{ columns: [field('Supporting indications', indications)] }]] },
        layout: fieldGridLayout,
      },
      sectionLabel('Diagnosis'),
      {
        stack: [
          {
            columns: [
              { text: value(data.diagnosisCode), style: 'diagnosisCode' },
              { text: value(data.diagnosis), style: 'fieldValue' },
              { text: 'PRIMARY', style: 'diagnosisType' },
            ],
          },
          ...(data.secondaryDiagnosis
            ? [
                {
                  columns: [
                    { text: value(data.secondaryDiagnosisCode), style: 'diagnosisCode' },
                    { text: data.secondaryDiagnosis, style: 'fieldValue' },
                    { text: 'SECONDARY', style: 'diagnosisType' },
                  ],
                },
              ]
            : []),
        ],
      },
      sectionLabel('Physician note'),
      {
        table: { widths: ['*'], body: [[{ text: value(data.notes) || '—', style: 'physicianNote' }]] },
        layout: calloutLayout,
      },
      sectionLabel('Ordered by'),
      {
        columns: [
          field('Ordering provider', providerName(data.practitioner)),
          field('NPI', practitionerNpi ?? ''),
          field('Practice', 'Polaris Sleep'),
        ],
      },
      { columns: [field('Contact', practitionerPhone ?? '')] },
      {
        text: 'I certify that this diagnostic study is medically necessary for this patient and that the information above is accurate.',
        style: 'certification',
      },
      {
        columns: [
          {
            stack: [
              { text: providerName(data.practitioner), style: 'signatureName' },
              {
                text: `Electronically signed ${data.orderDate} at ${formatSignedAt(data.signedAt)}`,
                style: 'signatureMeta',
              },
              { text: `NPI ${value(practitionerNpi)}`, style: 'signatureMeta' },
            ],
            style: 'signatureLine',
          },
          {
            stack: [
              { text: data.orderDate, style: 'signatureDate' },
              { text: 'Date of order', style: 'signatureMeta' },
            ],
            style: 'signatureLine',
          },
        ],
      },
      {
        text: 'Results are interpreted by a board-certified sleep physician and returned to Polaris Sleep. Questions: (301) 246-2152.',
        style: 'footer',
      },
    ],
    styles: {
      subtitle: { fontSize: 8, color: '#6b6169', characterSpacing: 1, margin: [0, 3, 0, 0] },
      clinicDetails: { fontSize: 8, color: '#6b6169', lineHeight: 1.5 },
      documentTitle: { fontSize: 14, bold: true, color: '#241d22', margin: [0, 14, 0, 2] },
      documentDate: { fontSize: 9, color: '#6b6169', margin: [0, 0, 0, 8] },
      sectionHeading: { fontSize: 8.5, bold: true, color: '#640040', characterSpacing: 1, margin: [0, 6, 0, 4] },
      fieldLabel: { fontSize: 7, color: '#6b6169', characterSpacing: 0.4, margin: [0, 0, 0, 1] },
      fieldValue: { fontSize: 9, color: '#241d22', margin: [0, 0, 0, 3] },
      orderCallout: {
        fontSize: 9,
        bold: true,
        color: '#241d22',
        fillColor: '#f7f2f5',
        margin: [13, 8, 13, 8],
      },
      physicianNote: { fontSize: 9, color: '#241d22', fillColor: '#f7f2f5', margin: [13, 8, 13, 8] },
      diagnosisCode: { width: 70, fontSize: 9, color: '#640040', bold: true },
      diagnosisType: { width: 80, fontSize: 8.5, color: '#6b6169', alignment: 'right', characterSpacing: 0.4 },
      certification: { fontSize: 9, margin: [0, 5, 0, 5], lineHeight: 1.5 },
      signatureLine: {
        margin: [0, 5, 25, 0],
        border: [false, true, false, false],
        borderColor: '#241d22',
        padding: [0, 7, 0, 0],
      },
      signatureName: { fontSize: 15, italics: true, font: 'Roboto', margin: [0, 0, 0, 4] },
      signatureDate: { fontSize: 15, margin: [0, 0, 0, 4] },
      signatureMeta: { fontSize: 8.5, color: '#6b6169', lineHeight: 1.5 },
      footer: {
        fontSize: 8,
        color: '#6b6169',
        margin: [0, 7, 0, 0],
        border: [false, true, false, false],
        borderColor: '#e6e1e4',
        padding: [0, 7, 0, 0],
      },
    },
    defaultStyle: { fontSize: 9, color: '#241d22', font: 'Roboto' },
  };
}

async function svgToPngDataUri(svg: string): Promise<string> {
  const objectUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  try {
    const image = new Image();
    image.src = objectUrl;
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Unable to load Polaris logo'));
    });
    const canvas = document.createElement('canvas');
    canvas.width = 294;
    canvas.height = 56;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Unable to prepare Polaris logo');
    }
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function formatSignedAt(signedAt: string): string {
  return new Date(signedAt).toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

function createPdfBlob(definition: Record<string, unknown>): Promise<Blob> {
  return Promise.all([import('pdfmake/build/pdfmake'), import('pdfmake/build/vfs_fonts')]).then(
    ([pdfMakeModule, pdfFontsModule]) => {
      const pdfMake = pdfMakeModule.default;
      const pdfFonts = pdfFontsModule.default;
      // pdfmake 0.2.x exports the font map directly, while older builds nest it under pdfMake.vfs.
      const pdfMakeRuntime = pdfMake as typeof pdfMake & {
        addVirtualFileSystem?: (fonts: Record<string, string>) => void;
      };
      const fonts = pdfFonts as unknown as Record<string, string>;
      if (pdfMakeRuntime.addVirtualFileSystem) {
        pdfMakeRuntime.addVirtualFileSystem(fonts);
      } else {
        pdfMake.vfs = pdfFonts.pdfMake?.vfs ?? pdfFonts.vfs ?? fonts;
      }
      return new Promise<Blob>((resolve, reject) => {
        try {
          pdfMake.createPdf(definition).getBlob(resolve);
        } catch (error) {
          reject(error);
        }
      });
    }
  );
}

export async function createPsgOrderDocument(
  medplum: MedplumClient,
  patient: Patient,
  practitioner: Practitioner,
  serviceRequest: ServiceRequest,
  data: PsgOrderPdfData
): Promise<DocumentReference> {
  const logoDataUri = await svgToPngDataUri(polarisLogo);
  const blob = await createPdfBlob(buildPsgOrderPdfDefinition(data, logoDataUri));
  const safePatientName = patientName(patient)
    .replaceAll(/[^a-z0-9]+/gi, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  const filename = `${safePatientName || 'patient'}-sleep-study-order-psg.pdf`;
  const isOtherHsat = data.orderType === 'hsat-other';
  const documentTitle = isOtherHsat ? HSAT_OTHER_DOCUMENT_TITLE : IN_LAB_PSG_DOCUMENT_TITLE;
  const documentType = isOtherHsat ? 'HSAT Sleep Study Order' : 'In-Lab PSG Sleep Study Order';
  const documentFilename = isOtherHsat ? `${safePatientName || 'patient'}-sleep-study-order-hsat.pdf` : filename;
  const binary = await medplum.createBinary(blob, documentFilename, 'application/pdf');
  return medplum.createResource<DocumentReference>(
    withTenantScope({
      resourceType: 'DocumentReference',
      status: 'current',
      subject: createReference(patient),
      date: data.signedAt,
      author: [createReference(practitioner)],
      type: { text: documentType },
      description: `${documentTitle}. Linked to ServiceRequest/${serviceRequest.id}.`,
      context: { related: [{ reference: `ServiceRequest/${serviceRequest.id}` }] },
      content: [
        {
          attachment: { title: documentTitle, url: `Binary/${binary.id}`, contentType: 'application/pdf' },
        },
      ],
    })
  );
}

export function coverageToPsgData(coverage: Coverage | undefined, payerName?: string): PsgCoverageData | undefined {
  if (!coverage) {
    return undefined;
  }
  const planClass = coverage.class?.find((entry) => entry.type?.coding?.some((coding) => coding.code === 'plan'));
  const groupClass = coverage.class?.find((entry) => entry.type?.coding?.some((coding) => coding.code === 'group'));
  return {
    payer: payerName ?? coverage.payor?.[0]?.display ?? '—',
    plan: planClass?.name ?? planClass?.value ?? '—',
    memberId: coverage.subscriberId ?? '—',
    group: groupClass?.value ?? '—',
  };
}
